/**
 * 设备健康。**这一块最容易撒谎**,所以规矩写在这里:
 *
 *  · 电量/温度来自设备遥测。没上报过的设备显示"待上报",不是 0%;
 *  · 顶部那句"N/M 台已上报"就是**分母的来历** —— 没有它,下面的读数看着像全队状况;
 *  · 阈值配色只对**测到的值**生效,未知一律中性色。
 */
import type { Terminal } from '@/types/fleet';
import { pct, degC, barWidth, timeAgo } from '@/lib/format';

export default function DeviceHealth({ terminals, reporting }: { terminals: Terminal[]; reporting: number }) {
  const live = terminals.filter((t) => !t.revoked);

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 h-full">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-foreground-800 font-heading">设备健康</h3>
        <span className="text-[11px] font-mono text-foreground-400">{reporting}/{live.length} 台已上报</span>
      </div>
      <p className="text-[10px] text-foreground-400 mb-3">
        电量与温度来自设备主动上报;没上报的显示「待上报」,不按 0 算。
      </p>

      {live.length === 0 ? (
        <p className="text-xs text-foreground-400 py-6 text-center">注册表里还没有设备</p>
      ) : (
        <div className="space-y-2.5 max-h-[300px] overflow-y-auto">
          {live.map((t) => {
            const hasTel = t.telemetryAt != null;
            return (
              <div key={t.id} className="px-3 py-2.5 rounded-lg bg-background-100/70">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${t.status === 'online' ? 'bg-accent-500' : 'bg-foreground-300'}`} />
                  <span className={`text-xs font-medium truncate ${t.status === 'online' ? 'text-foreground-800' : 'text-foreground-400'}`}>{t.name}</span>
                  <span className="ml-auto text-[10px] font-mono text-foreground-400 shrink-0">
                    {hasTel ? timeAgo(t.telemetryAt ?? null) : '待上报'}
                  </span>
                </div>
                {hasTel ? (
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      <div className="w-12 h-1.5 rounded-full bg-background-200 overflow-hidden shrink-0">
                        <div className={`h-full rounded-full ${t.battery == null ? 'bg-background-300' : t.battery <= 10 ? 'bg-red-500' : t.battery <= 25 ? 'bg-orange-500' : 'bg-accent-500'}`}
                          style={{ width: barWidth(t.battery) }} />
                      </div>
                      <span className={`text-[11px] font-mono ${t.battery != null && t.battery <= 10 ? 'text-red-600' : 'text-foreground-600'}`}>{pct(t.battery)}</span>
                    </div>
                    <span className={`text-[11px] font-mono shrink-0 ${t.temperature == null ? 'text-foreground-400' : t.temperature >= 80 ? 'text-red-500' : t.temperature >= 70 ? 'text-amber-600' : 'text-foreground-500'}`}>
                      {degC(t.temperature)}
                    </span>
                    <span className="text-[10px] font-mono text-foreground-400 shrink-0 truncate max-w-[70px]">{t.version ?? ''}</span>
                  </div>
                ) : (
                  <p className="text-[10px] text-foreground-400">这台设备还没上报过遥测</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
